import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { VerificationBadge } from "../components/VerificationBadge.js";

/**
 * A tournament: its terms, its entry button, and its public participant list.
 *
 * The participant list is a requirement of the regulations — it is published
 * in the FMJD calendar — so it is public and needs no sign-in.
 */

interface Requirement {
  code: string;
  message: string;
  action: string;
}

interface TournamentView {
  slug: string;
  name: string;
  description: string | null;
  variant: string;
  system: string;
  rounds: number;
  timeControl: { label: string };
  state: string;
  entrants: number;
  maxPlayers: number | null;
  requirements: {
    emailVerified: boolean;
    phoneVerified: boolean;
    fmjdId: boolean;
    verificationLevel: "none" | "contact" | "identity";
  };
  startsAt: string | null;
  registrationClosesAt: string | null;
  you?: {
    entered: boolean;
    registrationState: string | null;
    eligible: boolean;
    missing: Requirement[];
  };
}

export interface Participant {
  registrationId: number;
  state: "pending" | "confirmed" | "withdrawn" | "excluded";
  registeredAt: string;
  publicId: string;
  nickname: string;
  firstName: string;
  lastName: string;
  country: string | null;
  fmjdId: string | null;
  verificationLevel: "none" | "contact" | "identity";
  emailVerified: boolean;
  phoneVerified: boolean;
}

export function Tournament({ signedIn }: { signedIn: boolean }) {
  const { slug } = useParams();
  const [t, setT] = useState<TournamentView | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!slug) return;
    const [tRes, pRes] = await Promise.all([
      fetch(`/api/tournaments/${slug}`),
      fetch(`/api/tournaments/${slug}/participants`),
    ]);
    if (tRes.ok) setT(await tRes.json());
    if (pRes.ok) setParticipants((await pRes.json()).participants);
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  async function enter() {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/tournaments/${slug}/enter`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(
          data.error === "not_eligible"
            ? null // shown as the requirements list instead
            : data.error === "full"
              ? "The tournament is full."
              : data.error === "already_entered"
                ? "You are already entered."
                : "Could not enter, please try again."
        );
        if (data.missing) {
          setT((prev) => (prev ? { ...prev, you: { ...prev.you!, missing: data.missing } } : prev));
        }
        return;
      }
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function leave() {
    setBusy(true);
    await fetch(`/api/tournaments/${slug}/withdraw`, { method: "POST" }).catch(() => {});
    await load();
    setBusy(false);
  }

  if (!t) return <p style={{ color: "#666" }}>Loading…</p>;

  const active = participants.filter((p) => p.state === "confirmed" || p.state === "pending");

  return (
    <div>
      <h1 style={{ marginTop: 0, fontSize: 26 }}>{t.name}</h1>

      {t.description && (
        <p style={{ color: "#555", lineHeight: 1.6, whiteSpace: "pre-line", maxWidth: 640 }}>
          {t.description}
        </p>
      )}

      {isUnderway(t.state) && (
        <Link
          to={`/t/${t.slug}/live`}
          style={{
            display: "inline-block",
            padding: "10px 22px",
            borderRadius: 8,
            background: "#00695c",
            color: "#fff",
            textDecoration: "none",
            fontWeight: 600,
            marginBottom: 8,
          }}
        >
          {t.state === "finished"
            ? "Final standings and all games →"
            : "Pairings, your board and standings →"}
        </Link>
      )}

      <div style={{ display: "flex", gap: 28, flexWrap: "wrap", marginTop: 24 }}>
        <div style={{ minWidth: 280, flex: "0 0 300px" }}>
          <Facts t={t} />
          <EntryPanel
            t={t}
            signedIn={signedIn}
            busy={busy}
            error={error}
            onEnter={enter}
            onLeave={leave}
          />
        </div>

        <div style={{ flex: 1, minWidth: 320 }}>
          <h2 style={{ fontSize: 18, marginTop: 0 }}>
            Participants{" "}
            <span style={{ color: "#888", fontWeight: 400 }}>
              ({active.length}
              {t.maxPlayers ? ` of ${t.maxPlayers}` : ""})
            </span>
          </h2>
          <ParticipantTable participants={participants} />
        </div>
      </div>
    </div>
  );
}

function Facts({ t }: { t: TournamentView }) {
  const rows: [string, string][] = [
    ["Game", t.variant === "international" ? "International draughts 10×10" : t.variant],
    ["System", t.system === "swiss" ? `Swiss, ${t.rounds} rounds` : t.system],
    ["Time control", t.timeControl.label],
    ["Status", stateLabel(t.state)],
  ];
  if (t.startsAt) rows.push(["Starts", new Date(t.startsAt).toLocaleString()]);
  if (t.registrationClosesAt) {
    rows.push(["Entry closes", new Date(t.registrationClosesAt).toLocaleString()]);
  }

  return (
    <table style={{ width: "100%", fontSize: 14, borderCollapse: "collapse", marginBottom: 18 }}>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <td style={{ color: "#777", padding: "4px 12px 4px 0", verticalAlign: "top" }}>{k}</td>
            <td style={{ padding: "4px 0", fontWeight: 500 }}>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EntryPanel({
  t,
  signedIn,
  busy,
  error,
  onEnter,
  onLeave,
}: {
  t: TournamentView;
  signedIn: boolean;
  busy: boolean;
  error: string | null;
  onEnter: () => void;
  onLeave: () => void;
}) {
  const open = t.state === "registration_open";

  if (!open) {
    return (
      <Box tone="neutral">
        Entry is {t.state === "draft" ? "not yet open" : "closed"}.
      </Box>
    );
  }

  if (!signedIn) {
    return (
      <Box tone="neutral">
        <p style={{ margin: "0 0 10px" }}>Sign in to enter this tournament.</p>
        <Link to="/login" style={primaryButton}>
          Sign in
        </Link>{" "}
        <Link to="/register" style={{ fontSize: 14, color: "#00695c", fontWeight: 600 }}>
          or create an account
        </Link>
      </Box>
    );
  }

  if (t.you?.entered) {
    return (
      <Box tone="ok">
        <p style={{ margin: "0 0 10px", fontWeight: 600 }}>You are entered.</p>
        <button onClick={onLeave} disabled={busy} style={secondaryButton}>
          Withdraw
        </button>
      </Box>
    );
  }

  const missing = t.you?.missing ?? [];
  if (missing.length > 0) {
    // Each requirement links to the page that satisfies it. Telling a player
    // what is missing without saying where to fix it is a dead end.
    return (
      <Box tone="warn">
        <p style={{ margin: "0 0 8px", fontWeight: 600 }}>Before you can enter:</p>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.7 }}>
          {missing.map((m) => (
            <li key={m.code}>{m.message}</li>
          ))}
        </ul>
        <p style={{ margin: "12px 0 0" }}>
          <Link to="/account" style={primaryButton}>
            Go to your account
          </Link>
        </p>
      </Box>
    );
  }

  return (
    <Box tone="neutral">
      <button onClick={onEnter} disabled={busy} style={primaryButtonEl(busy)}>
        {busy ? "Entering…" : "Enter tournament"}
      </button>
      {error && <p style={{ color: "#c62828", fontSize: 14, margin: "10px 0 0" }}>{error}</p>}
    </Box>
  );
}

export function ParticipantTable({
  participants,
  extraColumn,
}: {
  participants: Participant[];
  extraColumn?: (p: Participant) => React.ReactNode;
}) {
  if (participants.length === 0) {
    return <p style={{ color: "#888", fontSize: 14 }}>No participants yet.</p>;
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 14, width: "100%" }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "2px solid #e0e0e0" }}>
            <th style={th}>#</th>
            <th style={th}>Player</th>
            <th style={th}>Name</th>
            <th style={th}>Country</th>
            <th style={th}>FMJD ID</th>
            <th style={th}>Verified</th>
            {extraColumn && <th style={th}></th>}
          </tr>
        </thead>
        <tbody>
          {participants.map((p, i) => {
            const inactive = p.state === "withdrawn" || p.state === "excluded";
            return (
              <tr
                key={p.registrationId}
                style={{
                  borderBottom: "1px solid #f0f0f0",
                  opacity: inactive ? 0.45 : 1,
                  textDecoration: inactive ? "line-through" : undefined,
                }}
              >
                <td style={{ ...td, color: "#999" }}>{inactive ? "—" : i + 1}</td>
                <td style={{ ...td, fontWeight: 600 }}>{p.nickname}</td>
                <td style={td}>
                  {p.firstName} {p.lastName}
                </td>
                <td style={td}>{p.country ?? "—"}</td>
                <td style={{ ...td, fontFamily: "ui-monospace, monospace" }}>
                  {p.fmjdId ?? "—"}
                </td>
                <td style={td}>
                  <VerificationBadge level={p.verificationLevel} />
                </td>
                {extraColumn && <td style={td}>{extraColumn(p)}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** True once pairings exist, so the live view is worth offering. */
function isUnderway(state: string): boolean {
  return [
    "ready",
    "round_running",
    "round_paused",
    "round_finished",
    "break",
    "finished",
  ].includes(state);
}

export function stateLabel(state: string): string {
  const labels: Record<string, string> = {
    draft: "Draft",
    registration_open: "Entry open",
    registration_closed: "Entry closed",
    ready: "Ready to start",
    round_running: "Round in progress",
    round_paused: "Paused",
    round_finished: "Round finished",
    break: "Break between rounds",
    finished: "Finished",
    abandoned: "Abandoned",
  };
  return labels[state] ?? state;
}

function Box({ tone, children }: { tone: "ok" | "warn" | "neutral"; children: React.ReactNode }) {
  const bg = tone === "ok" ? "#e8f5e9" : tone === "warn" ? "#fff8e1" : "#f5f5f5";
  const border = tone === "ok" ? "#a5d6a7" : tone === "warn" ? "#ffe082" : "#e0e0e0";
  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: 14 }}>
      {children}
    </div>
  );
}

const th: React.CSSProperties = { padding: "8px 12px 8px 0", fontWeight: 600, color: "#555" };
const td: React.CSSProperties = { padding: "8px 12px 8px 0" };

const primaryButton: React.CSSProperties = {
  display: "inline-block",
  padding: "9px 18px",
  fontSize: 14,
  fontWeight: 600,
  color: "#fff",
  background: "#00695c",
  borderRadius: 6,
  textDecoration: "none",
};

const primaryButtonEl = (busy: boolean): React.CSSProperties => ({
  padding: "10px 20px",
  fontSize: 15,
  fontWeight: 600,
  color: "#fff",
  background: busy ? "#90a4ae" : "#00695c",
  border: "none",
  borderRadius: 6,
  cursor: busy ? "default" : "pointer",
  width: "100%",
});

const secondaryButton: React.CSSProperties = {
  padding: "7px 14px",
  fontSize: 14,
  border: "1px solid #ccc",
  borderRadius: 6,
  background: "#fff",
  cursor: "pointer",
};
